
import { faCheck, faEye, faEyeSlash, faLock, faTrashCan, faUserMinus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AlertTitle, Box, Button, Checkbox, CircularProgress, IconButton, InputAdornment, Link, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, TextFieldProps, Typography, styled, useTheme } from '@mui/material';
import { grey } from '@mui/material/colors';
import { Dispatch, FC, HTMLInputTypeAttribute, ReactNode, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { CommunityGroupMemberStatus, Species, UpdateUser, UpdateUserEmailOptOut, UpdateUserGroupStatus, UpdateUserPassword, User, UserDelete } from '../api/dtos/dtos';
import Center from '../components/common/Center';
import Hr from '../components/common/HorizontalRule';
import SliderButton from '../components/common/buttons/SliderButton';
import { UserFormatError } from '../constants/exceptions/CustomExceptions';
import { ERROR_TIMEOUT, SUCCESS_TIMEOUT } from '../constants/styles/AlertConstants';
import AuthContext from '../contexts/auth/AuthContext';
import UserGroupsContext from '../contexts/data/UserGroupsContext';
import AlertContext from '../contexts/styles/AlertContext';
import useServiceStackClient from '../hooks/api/useServiceStackClient';
import { useLocation } from 'react-router-dom';


interface IStyledTableRowProps {
    primaryLabel: string;
    secondaryLabel?: string;

    type?: HTMLInputTypeAttribute;

    fieldState?: FieldState;

    select?: boolean;
    items?: ReactNode[];
    
    disabled?: boolean;

    value: any;
    onChange?: Dispatch<SetStateAction<any>>
}

type FieldState = {
    state?: "error" | "warning";
    message?: string;
}

const StyledTableCell = styled(TableCell)(() => ({
    padding: '8px',
    border: 'none'
}));

const StyledTextField = styled(TextField, { shouldForwardProp: (props) => props !== 'stateColor' })<TextFieldProps & { stateColor?: string }>(({ theme, stateColor, disabled }) => ({
    '& label': {
        color: stateColor === 'error' ?
                theme.palette.error.main :
            stateColor === 'warning' ?
                theme.palette.warning.main :
                grey[700],
    },
    '& p': {
        color: stateColor === 'error' ?
                theme.palette.error.main :
            stateColor === 'warning' ?
                theme.palette.warning.main :
                grey[700]
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: stateColor === 'error' ? 
                    `${theme.palette.error.main}77` :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                    'rgba(0, 0, 0, 0.2)',
        },
        '&:hover fieldset': {
            borderColor: stateColor === 'error' ? 
                    theme.palette.error.main :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                disabled ?
                    'rgba(0, 0, 0, 0.2)' :
                    'rgb(0, 0, 0)',
        },
        '&.Mui-focused fieldset': {
            borderColor: stateColor === 'error' ? 
                    theme.palette.error.main :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                    theme.palette.primary.main
        },
    },
}));

const ProfileContainer = styled(Box)(() => ({
    padding: '30px 14px 8px 14px'
}));

const StyledConfirmButton = styled(Button)(({ theme }) => ({
    maxWidth: '100px',
    minWidth: '100px',
    minHeight: '40px',
    maxHeight: '40px',
    backgroundColor: theme.palette.primary.main,
    color: grey[100],
    marginLeft: '30px',

    "&:hover": {
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
    },

    "&:active": {
        backgroundColor: theme.palette.secondary.main
    }
}));

const StyledCancelButton = styled(Button)(({ theme }) => ({
    minWidth: '100px',
    minHeight: '40px',
    maxHeight: '40px',
    backgroundColor: 'transparent',
    border: `3px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    marginLeft: '30px',

    "&:hover": {
        backgroundColor: 'transparent',
        boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
    },

    "&:active": {
        backgroundColor: theme.palette.secondary.main
    }
}));

const NotificationsTableRow = styled(TableRow)(() => ({
    height: '80px',

    "&:hover": {
        backgroundColor: '#eee'
    }
}));

const StyledGroupAdminLink = styled(Link)(({ theme }) => ({
    textDecoration: "none",
    color: theme.palette.secondary.main,
    fontWeight: 700,

    "&:hover": {
        cursor: "pointer",
        textDecoration: "none",
        color: theme.palette.primary.main,
    },

    "&:hover p": {
        color: theme.palette.primary.main,
    }
}));

const StyledTableRow: FC<IStyledTableRowProps> = ({
    primaryLabel, 
    secondaryLabel,
    type,
    select,
    items,
    fieldState,
    disabled,
    value,
    onChange
}) => {
    const [showPassword, setShowPassword] = useState<boolean>(false);

    return (
        <TableRow>
            <StyledTableCell sx={{ minWidth: '124px', maxWidth: '124px' }}>
                {primaryLabel}:
            </StyledTableCell>
            <StyledTableCell width='100%'>
                {
                    select ? 
                        <StyledTextField
                            select
                            stateColor={fieldState?.state}
                            color={fieldState?.state}
                            helperText={fieldState?.message}
                            disabled={!!disabled}
                            label={secondaryLabel ?? ""}
                            fullWidth
                            size='small'
                            value={value}
                            onChange={onChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        >
                            {items}
                        </StyledTextField> :
                        <StyledTextField
                            id={`Text-Field-${primaryLabel.replaceAll(' ', '-')}`}
                            inputProps={{
                                autoComplete: 'new-password',
                            }}
                            InputProps={{
                                endAdornment: type === 'password' ? (
                                    <InputAdornment position="end">
                                        <IconButton 
                                            disableRipple
                                            sx={{ width: '24px', heigh: '24px' }}    
                                            onClick={() => setShowPassword(prev => !prev)}
                                        >
                                            {
                                                showPassword ? 
                                                    <FontAwesomeIcon icon={faEye} style={{ fontSize: '1.2rem' }} /> : 
                                                    <FontAwesomeIcon icon={faEyeSlash} style={{ fontSize: '1.2rem' }} />
                                            }
                                        </IconButton>
                                    </InputAdornment>
                                ) :
                                disabled ? (
                                    <InputAdornment position="end">
                                        <FontAwesomeIcon icon={faLock} style={{ fontSize: '1.2rem' }}/>
                                    </InputAdornment>
                                ) : 
                                null
                            }}
                            autoComplete='new-password'
                            type={type === 'password' ? !showPassword ? 'password' : 'text' : type}
                            stateColor={fieldState?.state}
                            color={fieldState?.state}
                            helperText={fieldState?.message}
                            disabled={!!disabled}
                            label={secondaryLabel ?? ""}
                            fullWidth
                            size='small'
                            value={value}
                            onChange={onChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                }
            </StyledTableCell>
        </TableRow>
    )
}

const ProfilePage: FC = () => {

    const { hash } = useLocation();

    const theme = useTheme();

    const client = useServiceStackClient();

    const { user, refresh, openLogin } = useContext(AuthContext);
    const { addAlert } = useContext(AlertContext);
    const { data: groupsConfigData, loading: groupsConfigLoading, refresh: groupsConfigRefresh } = useContext(UserGroupsContext);

    // -- Profile state --
    const [userDetails, setUserDetails] = useState<User | null>(null);
    const [userDetailsState, setUserDetailsState] = useState<Record<string, FieldState>>({});

    // -- Password state --
    const [password, setPassword] = useState<{ password: string, confirmPassword: string }>({
        password: '',
        confirmPassword: ''
    });
    const [passwordState, setPasswordState] = useState<Record<string, FieldState>>({});

    // -- Notifications --
    const [notifOptOuts, setNotifOptOuts] = useState<Record<number, { optIn: boolean, loading: boolean, disabled: boolean }>>({});

    useEffect(() => {
        if (hash) {
            const scrollToElement = () => {
            const element = document.getElementById(hash.substring(1));
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            } else {
                // Retry after a short delay if the element isn't found yet
                setTimeout(scrollToElement, 100);
            }
        };
    
            scrollToElement();
        }
    }, [hash]);

    const states = useMemo(() => {
        return [
            <MenuItem key={"act"} value="ACT">ACT</MenuItem>,
            <MenuItem key={"nsw"} value="NSW">NSW</MenuItem>,
            <MenuItem key={"nt"} value="NT">NT</MenuItem>,
            <MenuItem key={"qld"} value="QLD">QLD</MenuItem>,
            <MenuItem key={"sa"} value="SA">SA</MenuItem>,
            <MenuItem key={"tas"} value="TAS">TAS</MenuItem>,
            <MenuItem key={"vic"} value="VIC">VIC</MenuItem>,
            <MenuItem key={"wa"} value="WA">WA</MenuItem>,
            <MenuItem key={"other"} value="OTHER">OTHER</MenuItem>
        ];
    }, []);

    const notifications = useMemo(() => {
        if (!!groupsConfigData?.emailNotificationOptOuts && !!groupsConfigData?.sightingType && groupsConfigData.emailNotifications) {
            return groupsConfigData.emailNotifications.map(x => {

                const speciesType = groupsConfigData!.sightingType!.find(st => st.sightingTypeId == x.sightingTypeId);
                const optedOut = !!groupsConfigData!.emailNotificationOptOuts!.find(ou => ou.emailNotificationConfigId === x.emailNotificationConfigId);
    
                return {
                    notificationId: x.emailNotificationConfigId,
                    name: x.emailNotificationDescription,
                    type: speciesType?.sightingTypeName,
                    species: Species[speciesType?.species ?? 99],
                    optIn: !optedOut,
                } as { notificationId?: number, name?: string, type?: string, species?: string, optIn?: boolean };
            })
        } 
        else return [];

    }, [groupsConfigData]);

    const groups = useMemo(() => {
        return groupsConfigData?.communityGroups ?? [];
    }, [groupsConfigData]);

    const pendingGroups = useMemo(() => {
        return groupsConfigData?.pendingCommunityGroups ?? [];
    }, [groupsConfigData]);

    // -- Functions --

    // Validate the user profile fields
    const validateUserField = useCallback((field: string, value: any, warn?: boolean): boolean => {
        // Confirm the field is a key of user type
        if (field as keyof User) {
            // Update user's full name
            if (field === 'fullName') {
                const valid = value && value !== ''

                if (!valid) {
                    setUserDetailsState(c => {
                        return {
                            ...c,
                            [field]: {
                                state: 'error',
                                message: 'Name is required.'
                            }
                        };
                    });
                } 
                else {
                    setUserDetailsState(c => {
                        delete c[field];
                        return c;
                    })
                }

                return valid;
            }
            else if (field === 'postCode') { // Update user postcode
                const valid = value && value !== '' && /^\d+$/.test(value);

                if (!valid) {
                    setUserDetailsState(c => {
                        return {
                            ...c,
                            [field]: {
                                state: 'error',
                                message: 'Postcode is required and must be numeric.'
                            }
                        };
                    });
                }
                else {
                    setUserDetailsState(c => {
                        delete c[field];
                        return c;
                    })
                }

                return valid;
            }
            else if (field === 'state') { // Update user state
                const valid = value && value !== ''

                if (!valid) {
                    setUserDetailsState(c => {
                        return {
                            ...c,
                            [field]: {
                                state: 'error',
                                message: 'State is required.'
                            }
                        };
                    });
                }
                else {
                    setUserDetailsState(c => {
                        delete c[field];
                        return c;
                    })
                }

                return valid;
            }
            // Update user's email
            else if (field === 'email') {
                const regex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/

                const valid = regex.test(value);
                const errorMessage = !value || value === '' ?
                    'Email is required.' :
                    'Invalid email.'

                if (!valid) {
                    if (warn) {
                        setUserDetailsState(c => {
                            return {
                                ...c,
                                [field]: {
                                    state: 'warning',
                                    message: 'Invalid email'
                                }
                            };
                        });
                    }
                    else {
                        setUserDetailsState(c => {
                            return {
                                ...c,
                                [field]: {
                                    state: 'error',
                                    message: errorMessage
                                }
                            };
                        });
                    }
                }
                else {
                    setUserDetailsState(c => {
                        delete c[field];
                        return c;
                    })
                }

                return valid;
            }
            // Otherwise, clear all errors/warnings
            else {
                setUserDetailsState({});
            }

            return true;
        }

        return false;

    }, [setUserDetailsState])

    const handleProfileChange = useCallback((field: string, value: any) => {
        validateUserField(field, value, true);

        setUserDetails(c => {
            return new User({
                ...c,
                [field]: value
            });
        })
    }, [validateUserField, setUserDetails]);

    const handleProfileSubmit = useCallback(async () => {

        if (
            validateUserField('fullName', userDetails?.fullName) &&
            validateUserField('postCode', userDetails?.postCode) &&
            validateUserField('state', userDetails?.state) &&
            validateUserField('email', userDetails?.email)
        ) {
            try {
                // Make update request
                const request = new UpdateUser({
                    ...userDetails
                });

                await client.post(request);

                // Refresh locally cached user details
                await refresh();

                // Alert user of success
                addAlert({
                    content: <AlertTitle>
                            Details have been updated.
                        </AlertTitle>,
                    dismissable: true,
                    severity: 'success',
                    timeout: SUCCESS_TIMEOUT
                });

            } catch (error: any) {
                if (error?.responseStatus) {
                    if (error.responseStatus.errorCode === UserFormatError.errorCode) {
                        let newState: Record<string, FieldState> = {};

                        for (var key of Object.keys(error.responseStatus.meta)) {
                            newState[key] = {
                                message: error.responseStatus.meta[key],
                                state: 'error'
                            }
                        }

                        addAlert({
                            content: <AlertTitle>
                                    {newState[Object.keys(newState)[0]].message}
                                </AlertTitle>,
                            dismissable: true,
                            severity: 'error',
                            timeout: ERROR_TIMEOUT
                        });

                        setUserDetailsState(newState);
                    }
                    else {
                        addAlert({
                            content: <AlertTitle>
                                    {error.responseStatus.message}
                                </AlertTitle>,
                            dismissable: true,
                            severity: 'error',
                            timeout: ERROR_TIMEOUT
                        });
                    }
                }
                else {
                    addAlert({
                        content: <AlertTitle>
                                {error.message}
                            </AlertTitle>,
                        dismissable: true,
                        severity: 'error',
                        timeout: ERROR_TIMEOUT
                    });
                }
            }
        }

    }, [client, refresh, userDetails, validateUserField, addAlert, setUserDetailsState]);

    const validateNewPassword = useCallback(() => {
        if (password.password !== password.confirmPassword) {
            setPasswordState({ 
                ['password']: {
                    message: undefined,
                    state: 'error'
                },
                ['confirmPassword']: {
                    message: 'Password fields must be the same.',
                    state: 'error'
                }
            });

            return false;
        }
        else {
            setPasswordState({});
        }

        return true;
    }, [password, setPasswordState])

    const handlePasswordChange = useCallback((field: string, value: string) => {
        setPassword(c => {
            return {
                ...c,
                [field]: value
            };
        });
    }, [setPassword]);

    const handlePasswordSubmit = useCallback(async () => {
        let valid = true;
        valid = validateNewPassword();

        if (valid) {
            try {
                // Make update request
                const request = new UpdateUserPassword({
                    password: password.password
                });

                await client.post(request);

                // Refresh locally cached user details
                await refresh();

                // Alert user of success
                addAlert({
                    content: <AlertTitle>
                            Password has been updated.
                        </AlertTitle>,
                    dismissable: true,
                    severity: 'success',
                    timeout: SUCCESS_TIMEOUT
                });

                // Clear password fields on success
                setPassword(c => {
                    return {
                        ...c,
                        password: '',
                        confirmPassword: ''
                    };
                });

            } catch (error: any) {
                if (error?.responseStatus) {
                    addAlert({
                        content: <AlertTitle>
                                {error.responseStatus.message}
                            </AlertTitle>,
                        dismissable: true,
                        severity: 'error',
                        timeout: ERROR_TIMEOUT
                    });

                    setPasswordState({
                        ['password']: {
                            message: undefined,
                            state: 'error'
                        },
                        ['confirmPassword']: {
                            message: error.responseStatus.message,
                            state: 'error'
                        }
                    });
                }
                else {
                    addAlert({
                        content: <AlertTitle>
                                {error.message}
                            </AlertTitle>,
                        dismissable: true,
                        severity: 'error',
                        timeout: ERROR_TIMEOUT
                    });
                }
            }
        }
    }, [client, refresh, addAlert, validateNewPassword, setPasswordState]);

    const handleOptOutChange = useCallback(async (emailNotifId: number, value: boolean) => {
        try {
            // Update the global notifs states so all others are disabled,
            // whilst the current is loading
            setNotifOptOuts({ ...Object.keys(notifOptOuts).reduce((obj, key) => {
                const idKey = parseInt(key);

                if (idKey !== emailNotifId) {
                    obj[idKey].disabled = true;
                }
                else {
                    obj[idKey].loading = true;
                }

                return obj;
            }, notifOptOuts) });

            const request = new UpdateUserEmailOptOut({
                optOut: !value,
                id: user!.id,
                emailNotificationConfigId: emailNotifId
            });

            await client.post(request);

            // If no error was thrown during the request, assume it was a success!
            // Revert notifs back to prev state and set new value of current
            setNotifOptOuts({ ...Object.keys(notifOptOuts).reduce((obj, key) => {
                const idKey = parseInt(key);

                if (idKey !== emailNotifId) {
                    obj[idKey].disabled = false;
                }
                else {
                    obj[idKey].disabled = false;
                    obj[idKey].loading = false;
                    obj[idKey].optIn = value;
                }

                return obj;
            }, notifOptOuts) });

        } catch {
            setNotifOptOuts({ ...Object.keys(notifOptOuts).reduce((obj, key) => {
                const idKey = parseInt(key);

                obj[idKey].disabled = false;
                obj[idKey].loading = false;

                return obj;

            }, notifOptOuts) });

            addAlert({
                content: <AlertTitle>
                    {
                        value ? 
                            "Failed to opt in of email" :
                            "Failed to opt out of email."
                    }
                </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
        }
    }, [user, client, notifOptOuts, setNotifOptOuts]);

    const handleGroupRetire = useCallback(async (communityGroupId: number) => {
        try {
            const request = new UpdateUserGroupStatus({
                communityGroupId: communityGroupId,
                status: CommunityGroupMemberStatus.Removed
            });

            await client.post(request);

            await groupsConfigRefresh();

            addAlert({
                content: <AlertTitle>
                    Successfully left group.
                </AlertTitle>,
                dismissable: true,
                severity: 'success',
                timeout: SUCCESS_TIMEOUT
            });
        } catch {
            addAlert({
                content: <AlertTitle>
                    Failed to leave group.
                </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
        }
    }, [client, addAlert]);

    const handleGroupDecline = useCallback(async (communityGroupId: number) => {
        try {
            const request = new UpdateUserGroupStatus({
                communityGroupId: communityGroupId,
                email: user?.email,
                status: CommunityGroupMemberStatus.Declined
            });

            await client.post(request);

            await groupsConfigRefresh();

            addAlert({
                content: <AlertTitle>
                    Successfully declined group invitation.
                </AlertTitle>,
                dismissable: true,
                severity: 'success',
                timeout: SUCCESS_TIMEOUT
            });
        } catch {
            addAlert({
                content: <AlertTitle>
                    Failed to declined group invitation.
                </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
        }
    }, [client, addAlert, user]);

    const handleGroupAccept = useCallback(async (communityGroupId: number) => {
        try {
            const request = new UpdateUserGroupStatus({
                communityGroupId: communityGroupId,
                email: user?.email,
                status: CommunityGroupMemberStatus.Accepted
            });

            await client.post(request);

            await groupsConfigRefresh();

            addAlert({
                content: <AlertTitle>
                    Successfully accepted group invitation.
                </AlertTitle>,
                dismissable: true,
                severity: 'success',
                timeout: SUCCESS_TIMEOUT
            });
        } catch {
            addAlert({
                content: <AlertTitle>
                    Failed to accepted group invitation.
                </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
        }
    }, [client, addAlert, user]);

    const handleUserDelete = useCallback(async () => {
        try {
            const request = new UserDelete();

            await client.delete(request);

            addAlert({
                content: <AlertTitle>
                    Request has been sent
                </AlertTitle>,
                dismissable: true,
                severity: 'success',
                timeout: SUCCESS_TIMEOUT
            });
        } catch {
            addAlert({
                content: <AlertTitle>
                    Failed to send request
                </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
        }
    }, [client])

    const resetDetails = () => setUserDetails(user);

    // Update user details when global user changes
    useEffect(() => {
        if (userDetails == null && user) {
            setUserDetails(user);
        }
    }, [user, userDetails]);

    // Update notification opt out states
    useEffect(() => {
        const configs = groupsConfigData?.emailNotifications;
        const optOuts = groupsConfigData?.emailNotificationOptOuts ?? [];
        
        if (configs) {
            const configStates = configs.reduce((a: Record<number, { optIn: boolean, loading: boolean, disabled: boolean }>, b) => {
                const key: number = b.emailNotificationConfigId
                if (!a[key]) {
                    a[key] = { optIn: !optOuts.find(x => x.emailNotificationConfigId == b.emailNotificationConfigId), loading: false, disabled: false };
                }

                return a;
            }, {});

            setNotifOptOuts(configStates);
        }

    }, [groupsConfigData])

    if (user && userDetails == null) {
        return (
            <ProfileContainer>
                <Center sx={{padding: "40px"}}>
                    <CircularProgress/>
                </Center>
            </ProfileContainer>
        );
    }

    if (!user) {
        return (
            <ProfileContainer>
                <Box 
                    sx={{
                        display: "flex", 
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                        minHeight: "50vh"
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            p: 4,
                            maxWidth: 400,
                            width: "100%",
                            textAlign: "center",
                            borderRadius: 2,
                            background: '#F5F5F5'
                        }}
                    >
                        <Typography variant="h5" color="text.primary" gutterBottom>
                            Access Restricted
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            You must be logged in to view this page.
                        </Typography>

                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => openLogin()}
                            sx={{ color: "white" }}
                        >
                            Log In
                        </Button>
                    </Paper>
                </Box>
            </ProfileContainer>
        );
    }

    return (
        <ProfileContainer>
            {userDetails && (
                <>
                    {/* -- User Details -- */}
                    <Typography color="#333" variant='h2'>Profile</Typography>

                    <Hr color={grey[400]} sx={{ margin: '40px 0' }} />

                    <Table>
                        <TableBody>
                            <StyledTableRow
                                primaryLabel="Name"
                                fieldState={userDetailsState['fullName']}
                                value={userDetails!.fullName}
                                onChange={(v) => handleProfileChange('fullName', v.target.value)}
                            />
                            <StyledTableRow
                                primaryLabel="Username"
                                fieldState={userDetailsState['userName']}
                                disabled
                                value={userDetails!.userName}
                            />
                            <StyledTableRow
                                primaryLabel="Email"
                                fieldState={userDetailsState['email']}
                                value={userDetails!.email}
                                onChange={(v) => handleProfileChange('email', v.target.value)}
                            />
                            <StyledTableRow
                                primaryLabel="Mobile"
                                fieldState={userDetailsState['mobile']}
                                secondaryLabel="Optional"
                                value={userDetails!.mobile}
                                onChange={(v) => handleProfileChange('mobile', v.target.value)}
                            />
                            <StyledTableRow
                                primaryLabel="Postcode"
                                secondaryLabel="Numbers Only"
                                fieldState={userDetailsState['postCode']}
                                value={userDetails!.postCode}
                                onChange={(v) => handleProfileChange('postCode', v.target.value)}
                            />
                            <StyledTableRow
                                select
                                items={states}
                                primaryLabel="State"
                                fieldState={userDetailsState['state']}
                                value={userDetails!.state}
                                onChange={(v) => handleProfileChange('state', v.target.value)}
                            />
                            <TableRow>
                                <StyledTableCell sx={{ minWidth: '124px', maxWidth: '124px' }}>
                                    Notifications:
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Checkbox checked={!!userDetails.beNotified} onChange={(v) => handleProfileChange('beNotified', v.target.checked)}/>
                                    <Typography display='inline'>I wish to be notified about updates to FeralScan</Typography>
                                </StyledTableCell>
                            </TableRow>
                        </TableBody>
                    </Table>

                    <Center ha='end' m='15px 10px'>
                        <StyledConfirmButton
                            onClick={handleProfileSubmit}
                        >
                            <Typography fontWeight='600'>Update</Typography>
                        </StyledConfirmButton>
                        <StyledCancelButton
                            onClick={resetDetails}
                        >
                            <Typography fontWeight='600'>Reset</Typography>
                        </StyledCancelButton>
                    </Center>

                    <Hr color={grey[400]} sx={{ margin: '40px 0' }} />

                    {/* -- Change Password -- */}
                    <Typography color="#333" variant='h2' mb='40px'>Change your password:</Typography>

                    <Table>
                        <TableBody>
                            <StyledTableRow
                                type="password"
                                primaryLabel="New Password"
                                fieldState={passwordState['password']}
                                value={password.password}
                                onChange={(v) => handlePasswordChange('password', v.target.value)}
                            />
                            <StyledTableRow
                                type="password"
                                primaryLabel="Confirm Password"
                                fieldState={passwordState['confirmPassword']}
                                value={password.confirmPassword}
                                onChange={(v) => handlePasswordChange('confirmPassword', v.target.value)}
                            />
                        </TableBody>
                    </Table>

                    <Center ha='end' m='15px 10px'>
                        <StyledConfirmButton
                            onClick={() => handlePasswordSubmit()}
                        >
                            <Typography fontWeight='600'>Change</Typography>
                        </StyledConfirmButton>
                    </Center>

                    <Hr color={grey[400]} sx={{ margin: '40px 0' }} />

                    {/* -- Alert Notifications -- */}
                    <Typography color="#333" variant='h2' mb='40px'>Notification settings:</Typography>

                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#ccc' }}>
                                <TableCell>
                                    <Typography color='#333' variant='h5' fontWeight='bold'>Notification</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography color='#333' variant='h5' fontWeight='bold'>Type</Typography>
                                </TableCell>
                                <TableCell align='right'>
                                    <Typography color='#333' variant='h5' fontWeight='bold'>Opt Out / In</Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                notifications && 
                                notifications.map((x, idx) => (
                                    <NotificationsTableRow key={`notif-${idx}`}>
                                        <TableCell>{x.name}</TableCell>
                                        <TableCell>{x.species} - {x.type}</TableCell>
                                        <TableCell align='right'>
                                            <SliderButton 
                                                key={`notif-slider-${idx}`}
                                                value={ notifOptOuts[(x?.notificationId ?? -1)]?.optIn }
                                                loading={ notifOptOuts[(x?.notificationId ?? -1)]?.loading || groupsConfigLoading }
                                                disabled={ notifOptOuts[(x?.notificationId ?? -1)]?.disabled || groupsConfigLoading }
                                                onChange={(value: boolean) => handleOptOutChange(x?.notificationId ?? -1, value)}
                                                onColor={theme.palette.success.main}
                                            />
                                        </TableCell>
                                    </NotificationsTableRow>
                                ))
                            }
                            
                        </TableBody>
                    </Table>
                    {
                        groupsConfigLoading && 
                        <Center mt='45px'>
                            <CircularProgress />
                        </Center> 
                    }

                    <Hr color={grey[400]} sx={{ margin: '40px 0' }} />

                    {/* -- Pending Groups -- */}
                    <Typography id="pendinggroupinvites" color="#333" variant='h2' mb='10px'>Pending Groups:</Typography>

                    {
                        pendingGroups && pendingGroups.length > 0 ? 
                            <>
                                <Typography mb='20px'>You currently have pending group invitations. You can choose whether to accept or decline each group invitation.</Typography>
                                <Table>
                                    <TableBody>
                                        {
                                            pendingGroups.map((x) => (
                                                <TableRow key={`pending-group-${x.communityGroupId}`}>
                                                    <StyledTableCell>
                                                        <Center ha='start'>
                                                            <Typography color='#222' fontWeight='bold' variant='h5' display='inline'>{x.name} </Typography>
                                                            {
                                                                x.adminUserEmail ?
                                                                    <StyledGroupAdminLink href={`mailto:${x.adminUserEmail}`}>
                                                                        <Typography 
                                                                            color={theme.palette.secondary.main} 
                                                                            display='inline'
                                                                        >
                                                                            &nbsp;(Email the Group Administrator)
                                                                        </Typography>
                                                                    </StyledGroupAdminLink>:
                                                                    null
                                                            }
                                                        </Center>
                                                        <StyledCancelButton 
                                                            sx={{ margin: '8px 8px' }}
                                                            onClick={() => {
                                                                handleGroupDecline(x.communityGroupId);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon fontSize='1.2rem' icon={faXmark} style={{ marginRight: '10px'}} />
                                                            <Typography fontWeight='600'>Decline</Typography>
                                                        </StyledCancelButton>
                                                        <StyledCancelButton 
                                                            sx={{ margin: '8px 8px' }}
                                                            onClick={() => {
                                                                handleGroupAccept(x.communityGroupId);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon fontSize='1.2rem' icon={faCheck} style={{ marginRight: '10px'}} />
                                                            <Typography fontWeight='600'>Accept</Typography>
                                                        </StyledCancelButton>
                                                    </StyledTableCell>
                                                </TableRow>
                                            ))
                                        }
                                        
                                    </TableBody>
                                </Table> 
                            </> :
                            <Typography>You currently have no pending group invitations.</Typography>
                    }

                    <Hr color={grey[400]} sx={{ margin: '40px 0' }} />

                    {/* -- Groups -- */}
                    <Typography color="#333" variant='h2' mb='40px'>Groups:</Typography>

                    <Table>
                        <TableBody>
                            {
                                groups && 
                                groups.map((x) => (
                                    <TableRow key={`group-${x.communityGroupId}`}>
                                        <StyledTableCell>
                                            <Center ha='start'>
                                                <Typography color='#222' fontWeight='bold' variant='h5' display='inline'>{x.name} </Typography>
                                                {
                                                    x.adminUserEmail ?
                                                        <StyledGroupAdminLink href={`mailto:${x.adminUserEmail}`}>
                                                            <Typography 
                                                                color={theme.palette.secondary.main} 
                                                                display='inline'
                                                            >
                                                                &nbsp;(Email the Group Administrator)
                                                            </Typography>
                                                        </StyledGroupAdminLink>:
                                                        null
                                                }
                                            </Center>
                                            <StyledCancelButton 
                                                sx={{ margin: '8px 0' }}
                                                onClick={() => {
                                                    handleGroupRetire(x.communityGroupId);
                                                }}
                                            >
                                                <FontAwesomeIcon fontSize='1.2rem' icon={faUserMinus} style={{ marginRight: '10px'}} />
                                                <Typography fontWeight='600'>Retire from Group</Typography>
                                            </StyledCancelButton>
                                        </StyledTableCell>
                                    </TableRow>
                                ))
                            }
                            
                        </TableBody>
                    </Table>

                    {/* -- Delete -- */}
                    <Center ha='end' m='15px 10px'>
                        <StyledCancelButton
                            onClick={handleUserDelete}
                        >
                            <FontAwesomeIcon fontSize='1.2rem' icon={faTrashCan} style={{ marginRight: '10px'}} />
                            <Typography fontWeight='600'>Delete user</Typography>
                        </StyledCancelButton>
                    </Center>
                </>
            )}
        </ProfileContainer>
    )
}

export default ProfilePage